/**
 * @file DesktopDropdownMenuContainer — behavior wiring for the shared menu.
 * @description Applies prop defaults, owns the `useDropdownPositioning` wiring
 * for the `FixedPositionDropdownShell` surface (SE-5570), and decorates each
 * item's `onPress` so a row press fires the caller handler and then requests
 * close (disabled rows are inert). The decorated items are memoized so the
 * view receives stable handler references. No data fetching — this is a
 * cross-feature presentational primitive (SE-5087).
 * @module components/molecules/DesktopDropdownMenu/DesktopDropdownMenuContainer
 */
import React, { useEffect, useMemo } from "react";
import { Platform } from "react-native";

import { useDropdownPositioning } from "@/components/atoms/FixedPositionDropdownShell";

import DesktopDropdownMenuView, {
  DESKTOP_DROPDOWN_MENU_DEFAULT_WIDTH,
  DESKTOP_DROPDOWN_MENU_MAX_HEIGHT,
  type DesktopDropdownMenuItem,
  type DesktopDropdownMenuProps,
} from "./DesktopDropdownMenuView";

/**
 * DesktopDropdownMenu — shared desktop dropdown-menu primitive.
 *
 * Anchors to the caller-supplied trigger content and renders a fixed-width
 * menu surface with consistent chrome (210px width, 45px rows, drop shadow,
 * rounded-lg) on the `FixedPositionDropdownShell` atom: measured
 * trigger-anchored positioning, viewport clamping with automatic flip-above,
 * outside-tap/hardware-back dismissal, and the native Modal host that stacks
 * above elevated app chrome (SE-5568).
 * @param props - Public menu props.
 * @param props.compact - Uses the compact table action menu density.
 * @param props.isOpen - Whether the menu is open.
 * @param props.onClose - Invoked when the menu requests close.
 * @param props.onToggle - Toggles the menu (trigger press).
 * @param props.triggerContent - Anchor visual content.
 * @param props.triggerTestID - testID for the trigger pressable.
 * @param props.triggerAccessibilityLabel - A11y label for the trigger.
 * @param props.triggerClassName - Classes for the trigger pressable.
 * @param props.items - Rows to render.
 * @param props.content - Custom panel body rendered instead of `items`.
 * @param props.maxHeight - Panel max height in px (defaults to 400).
 * @param props.remeasureKey - Re-anchors the open panel when it changes.
 * @param props.placement - Surface placement (defaults to "bottom-end").
 * @param props.width - Menu width in px (defaults to 210).
 * @param props.showPointer - Whether to render the pointer tail (defaults false).
 * @returns The anchored dropdown menu.
 */
const DesktopDropdownMenuContainer = ({
  compact = false,
  isOpen,
  onClose,
  onToggle,
  triggerContent,
  triggerTestID,
  triggerAccessibilityLabel,
  triggerClassName,
  items,
  content,
  maxHeight = DESKTOP_DROPDOWN_MENU_MAX_HEIGHT,
  remeasureKey,
  placement = "bottom-end",
  width = DESKTOP_DROPDOWN_MENU_DEFAULT_WIDTH,
  showPointer = false,
}: DesktopDropdownMenuProps): React.ReactElement => {
  const { triggerRef, panelGeometry, shouldShow } = useDropdownPositioning({
    isOpen,
    anchor: placement.endsWith("-end") ? "right" : "left",
    panelWidth: width,
    panelHeight: maxHeight,
    remeasureKey,
  });
  const decoratedItems = useMemo<DesktopDropdownMenuItem[]>(
    () =>
      items.map(item => ({
        ...item,
        onPress: (): void => {
          if (item.disabled) return;
          item.onPress();
          onClose();
        },
      })),
    [items, onClose]
  );
  useEffect(() => {
    if (!isOpen || Platform.OS !== "web") return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const trigger = triggerRef.current;
      event.preventDefault();
      event.stopPropagation();
      onClose();
      if (trigger instanceof HTMLElement) trigger.focus();
    };
    window.addEventListener("keydown", dismiss, true);
    return () => window.removeEventListener("keydown", dismiss, true);
  }, [isOpen, onClose, triggerRef]);

  return (
    <DesktopDropdownMenuView
      compact={compact}
      isOpen={isOpen}
      onClose={onClose}
      onToggle={onToggle}
      triggerContent={triggerContent}
      triggerTestID={triggerTestID}
      triggerAccessibilityLabel={triggerAccessibilityLabel}
      triggerClassName={triggerClassName}
      items={decoratedItems}
      content={content}
      placement={placement}
      showPointer={showPointer}
      triggerRef={triggerRef}
      panelGeometry={panelGeometry}
      shouldShow={shouldShow}
    />
  );
};

DesktopDropdownMenuContainer.displayName = "DesktopDropdownMenuContainer";

export default DesktopDropdownMenuContainer;
